import { useState } from 'react';
import {
  DndContext, DragOverlay, PointerSensor, KeyboardSensor,
  closestCenter, useSensor, useSensors,
  useDroppable,
  type DragEndEvent, type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Trash2 } from 'lucide-react';
import { getBlock, type BlockDefinition, type BlockInstance } from '@sytel/blocks';
import { BlockPalette } from './BlockPalette';
import { BlockList } from './BlockList';
import { BlockInspector } from './BlockInspector';
import { usePageEditor } from './usePageEditor';
import { cn } from '@/lib/cn';

export function PageBuilder() {
  const editor = usePageEditor();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeDef, setActiveDef] = useState<BlockDefinition | null>(null);
  const [activeBlock, setActiveBlock] = useState<BlockInstance | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragStart(event: DragStartEvent) {
    const { active } = event;
    setActiveId(String(active.id));
    const source = active.data.current?.source;
    if (source === 'palette') {
      const type = active.data.current?.blockType as string;
      setActiveDef(getBlock(type) ?? null);
      setActiveBlock(null);
    } else if (source === 'list') {
      const block = editor.blocks.find((b) => b.id === active.id);
      setActiveBlock(block ?? null);
      setActiveDef(block ? getBlock(block.type) ?? null : null);
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    setActiveId(null);
    setActiveDef(null);
    setActiveBlock(null);

    if (!over) return;

    const source = active.data.current?.source as 'palette' | 'list' | undefined;
    const overId = String(over.id);

    // Trash zone - remove block (only from list)
    if (overId === 'trash-zone' && source === 'list') {
      editor.removeBlock(String(active.id));
      return;
    }

    if (source === 'palette') {
      const type = active.data.current?.blockType as string;
      const def = getBlock(type);
      if (!def) return;

      const targetIdx = resolveInsertIndex(overId, editor.blocks);
      if (targetIdx == null) return;

      const newBlock: BlockInstance = {
        id: typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${type}-${Date.now()}`,
        type: def.meta.type,
        order: targetIdx,
        locale: editor.locale,
        visible: true,
        data: structuredClone(def.defaults) as Record<string, unknown>,
      };
      editor.insertBlock(newBlock, targetIdx);
      return;
    }

    if (source === 'list') {
      const fromIdx = editor.blocks.findIndex((b) => b.id === active.id);
      if (fromIdx < 0) return;
      const targetIdx = resolveInsertIndex(overId, editor.blocks);
      if (targetIdx == null) return;
      // When moving down, adjust because removing the source first shifts indices
      const finalIdx = targetIdx > fromIdx ? targetIdx - 1 : targetIdx;
      if (finalIdx === fromIdx) return;
      editor.reorder(fromIdx, finalIdx);
    }
  }

  function handleAdd(def: BlockDefinition) {
    const newBlock: BlockInstance = {
      id: typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${def.meta.type}-${Date.now()}`,
      type: def.meta.type,
      order: editor.blocks.length,
      locale: editor.locale,
      visible: true,
      data: structuredClone(def.defaults) as Record<string, unknown>,
    };
    editor.addBlock(newBlock);
  }

  const isDragging = activeId != null;
  const sortableIds = editor.blocks.map((b) => b.id);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => { setActiveId(null); setActiveDef(null); setActiveBlock(null); }}
    >
      <div className="flex flex-1 overflow-hidden">
        <BlockPalette onAdd={handleAdd} />
        <main className="relative flex-1 overflow-y-auto bg-neutral-50 p-6">
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            <BlockList />
          </SortableContext>
          <TrashZone visible={isDragging && activeBlock != null} />
        </main>
        <BlockInspector />
      </div>

      <DragOverlay dropAnimation={{ duration: 200 }}>
        {activeDef && (
          <div className="cursor-grabbing rounded-lg border-2 border-neutral-900 bg-white p-3 shadow-2xl">
            <div className="text-sm font-medium">{activeDef.meta.label}</div>
            <div className="line-clamp-2 text-xs text-neutral-500">
              {activeDef.meta.description}
            </div>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

function TrashZone({ visible }: { visible: boolean }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'trash-zone' });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2 rounded-full border-2 px-5 py-3 text-sm font-medium shadow-lg transition-all',
        visible ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
        isOver
          ? 'border-red-600 bg-red-600 text-white scale-110'
          : 'border-red-200 bg-white text-red-600',
      )}
    >
      <Trash2 className="size-4" />
      {isOver ? 'Soltar para eliminar' : 'Arrastra aquí para eliminar'}
    </div>
  );
}

/**
 * Given the "over" id from a drag event, compute the insertion index in the blocks array.
 * - `dropzone-N`        → insert at index N (explicit insertion slot)
 * - `dropzone-empty`    → insert at 0 (empty list)
 * - block UUID          → insert before that block (its current index)
 */
function resolveInsertIndex(overId: string, blocks: BlockInstance[]): number | null {
  if (overId === 'dropzone-empty') return 0;
  if (overId.startsWith('dropzone-')) {
    const n = Number(overId.slice('dropzone-'.length));
    return Number.isFinite(n) ? n : null;
  }
  const idx = blocks.findIndex((b) => b.id === overId);
  return idx < 0 ? blocks.length : idx;
}
